<template>
  <div class="flex flex-col h-full min-h-0 bg-gray-50/40">
    <LoadingIndicator v-if="loading" />

    <p v-else-if="error" class="p-4 text-sm text-red-dark">{{ error }}</p>

    <template v-else>
      <!-- HEADER — provenance + tabs, on the page surface not a card -->
      <div class="flex-none px-10 pt-5 bg-white border-b border-gray-200">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h1 class="text-lg font-semibold text-gray-900 leading-tight">Hades</h1>
            <p class="text-xs text-gray-600 mt-0.5">{{ metaLine }}</p>
          </div>
          <button
            type="button"
            class="text-xs text-primary-700 hover:underline"
            @click="tab === 'deadlines' ? fetchDeadlines() : fetchPortfolio()"
          >Refresh</button>
        </div>

        <div class="flex gap-6 mt-4 -mb-px">
          <button
            v-for="t in tabs"
            :key="t.key"
            type="button"
            class="pb-3.5 text-sm border-b-2"
            :class="tab === t.key
              ? 'text-gray-900 font-semibold border-primary-500'
              : 'text-gray-600 font-medium border-transparent'"
            @click="tab = t.key"
          >
            {{ t.label }}
            <span class="text-xs text-gray-600 font-normal ml-0.5">{{ t.count }}</span>
          </button>
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-auto px-10 pt-5 pb-10">
        <!-- STATS. Every stat here is derived from customerTickets /
             customerPrs — the portfolio payload's scope, not the deadlines
             endpoint's wider one — so it is hidden on this tab rather than
             shown frozen or, worse, silently describing the wrong scope. -->
        <div
          v-if="tab !== 'deadlines'"
          class="bg-white border border-gray-200 rounded-lg p-5 mb-5
                 flex flex-wrap gap-10"
        >
          <div v-for="stat in stats" :key="stat.label">
            <div class="text-[22px] font-semibold text-gray-900 leading-none">
              {{ stat.value }}
            </div>
            <div class="text-xs text-gray-600 mt-1">{{ stat.label }}</div>
          </div>
        </div>

        <!-- CUSTOMER PILLS, with pin + "N more" overflow per app_ui/components.
             HIDDEN ON DEADLINES: the payload has no tenant dimension at all,
             so a pill here would be a control that visibly does nothing —
             the exact silent no-op the invariants for this tab warn against.
             Requirements and Engineering are unchanged; Engineering already
             tolerates the pills doing nothing to its PRs (customerPrs), which
             this tab does not reproduce. -->
        <div v-if="tab !== 'deadlines'" class="flex items-center flex-wrap gap-2 mb-5">
          <span class="text-xs font-medium text-gray-600 mr-2">Customer</span>
          <!-- MULTI-SELECT: the pills toggle, so two tenants can be held
               against each other and the flow chart's "By customer" split has
               something to split. "All" is the absence of a selection, not a
               value, so it is cleared rather than selected. -->
          <button
            type="button"
            :class="pillClass(!selectedCustomers.length)"
            @click="clearCustomers"
          >
            All<span :class="badgeClass(!selectedCustomers.length)">{{ tickets.length }}</span>
          </button>
          <button
            v-for="card in shownCustomers"
            :key="card.customer"
            type="button"
            :aria-pressed="selectedCustomers.includes(card.customer)"
            :class="customerPillClass(selectedCustomers.includes(card.customer))"
            :style="tenantColorFor(card.customer)"
            @click="toggleCustomer(card.customer)"
          >
            <!-- Tenant name, masked in Demo Mode. 'Platform' is the
                 coalesced no-tenant bucket, not a customer. -->
            <span :class="{ sensitive: card.customer !== 'Platform' }">{{ card.customer }}</span>
            <span class="text-[11px] opacity-70">{{ card.total }}</span>
          </button>
          <button
            v-if="hiddenCustomerCount > 0 || showMoreCustomers"
            type="button"
            class="flex items-center h-[30px] px-3 rounded-full text-xs text-gray-600
                   border border-dashed border-gray-300 hover:border-gray-400"
            @click="showMoreCustomers = !showMoreCustomers"
          >
            {{ showMoreCustomers ? 'Show less' : `${hiddenCustomerCount} more` }}
          </button>
          <button
            v-if="selectedCustomers.length"
            type="button"
            class="text-xs text-primary-700 hover:underline ml-1"
            @click="clearCustomers"
          >Reset</button>
        </div>

        <!-- CHART CARD. Not a forecast, and this tab is not one either — it
             has no chart, so the card is hidden rather than shown against
             flow/pullRequests data that says nothing about due dates. -->
        <section v-if="boardTab" class="bg-white border border-gray-200 rounded-lg mb-5">
          <div
            class="flex items-start justify-between gap-4 px-5 py-4
                   border-b border-gray-200"
          >
            <div>
              <div class="text-base font-semibold text-gray-900">
                {{ tab === 'requirements' ? 'Ticket flow' : 'Engineering work' }}
              </div>
              <div class="text-xs text-gray-600 mt-0.5">
                {{ tab === 'requirements'
                  ? 'Weekly tickets in and out, open backlog on the right axis.'
                  : 'Pull requests merged per week, by category.' }}
              </div>
            </div>
            <button
              type="button"
              class="w-6 h-6 rounded text-gray-600 hover:bg-gray-50 flex-none"
              :title="chartsExpanded ? 'Collapse' : 'Expand'"
              @click="chartsExpanded = !chartsExpanded"
            >{{ chartsExpanded ? '⌃' : '⌄' }}</button>
          </div>
          <div v-if="chartsExpanded" class="px-5 pt-3 pb-4">
            <Hades_Charts
              :flow="flow"
              :pull-requests="pullRequests"
              :customers="selectedCustomers"
              :tab="tab"
            />
          </div>
        </section>

        <!-- BOARD CONTROLS. Every control here is generated from
             HADES_FILTERS — there is no per-filter markup, so a fifth filter is
             one entry in that list rather than another dropdown wired by hand
             to another computed and another watcher. -->
        <div
          v-if="tab === 'requirements'"
          class="flex items-center flex-wrap gap-2 mb-2"
        >
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search id, title or verdict…"
              aria-label="Search requirements"
              class="h-[30px] w-64 border border-gray-300 rounded pl-2.5 pr-7
                     text-xs text-gray-800 bg-white"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="absolute right-2 inset-y-0 text-xs text-gray-600 hover:text-gray-900"
              title="Clear search"
              @click="searchQuery = ''"
            >&times;</button>
          </div>
          <!-- QUICK FILTERS. Shorthands over the same pipeline, not a parallel
               one — All / Mine / Unowned / Overdue / P0-P1, read for a board
               about neglected work off the Work tab's four. -->
          <div class="flex rounded border border-gray-300 overflow-hidden">
            <button
              v-for="(quick, i) in quickFilterOptions"
              :key="quick.key || 'all'"
              type="button"
              class="text-xs px-2 h-[28px]"
              :class="[
                i ? 'border-l border-gray-300' : '',
                quickFilter === quick.key
                  ? 'bg-primary-100 text-primary-700 font-semibold'
                  : 'text-gray-600 hover:text-gray-900',
              ]"
              @click="setQuickFilter(quick.key)"
            >{{ quick.label }}<span
              v-if="quick.count !== undefined"
              class="ml-1 font-semibold"
              :class="quick.count ? 'text-red-dark' : 'text-gray-400'"
            >{{ quick.count }}</span></button>
          </div>
          <div class="flex-1" />
          <Hades_FilterSelect
            v-for="control in setFilterControls"
            :key="control.def.key"
            :label="control.def.label"
            :options="control.options"
            :model-value="filters[control.def.key]"
            :sensitive="control.def.sensitive || null"
            @update:model-value="setFilter(control.def.key, $event)"
          />
          <!-- A RANGE, not an Overdue checkbox. Two bounds on one field is what
               the date `kind` exists for; Overdue is one point in that range and
               lives on the quick filters. -->
          <div
            v-for="def in dateFilterDefs"
            :key="def.key"
            class="flex items-center gap-1.5 h-[30px] px-2.5 rounded border text-xs"
            :class="dateBoundsSet(def.key)
              ? 'border-primary-500 bg-primary-100 text-primary-700 font-semibold'
              : 'border-gray-300 bg-white text-gray-800'"
          >
            {{ def.label }}
            <input
              type="date"
              class="bg-transparent text-xs text-gray-800 font-normal"
              :value="filters[def.key].on_or_after || ''"
              :aria-label="`${def.label} on or after`"
              @change="setDateBound(def.key, 'on_or_after', $event.target.value)"
            />
            <span class="text-gray-600 font-normal">–</span>
            <input
              type="date"
              class="bg-transparent text-xs text-gray-800 font-normal"
              :value="filters[def.key].on_or_before || ''"
              :aria-label="`${def.label} on or before`"
              @change="setDateBound(def.key, 'on_or_before', $event.target.value)"
            />
            <button
              v-if="dateBoundsSet(def.key)"
              type="button"
              class="text-primary-700 font-normal"
              :title="`Clear ${def.label}`"
              @click="setFilter(def.key, emptyFilterValue(def.key))"
            >&times;</button>
          </div>
        </div>

        <!-- ACTIVE FILTER CHIPS. The Work tab also colours a contradictory
             any_of/none_of pair red; not ported, because none_of is not
             reachable from this UI — every set filter here is any_of. -->
        <div
          v-if="tab === 'requirements' && filterChips.length"
          class="flex flex-wrap items-center gap-1.5 mb-2"
        >
          <Tag
            v-for="chip in filterChips"
            :key="chip.id"
            :label="chip.label"
            :removable="true"
            @remove="chip.remove()"
          />
          <button
            type="button"
            class="text-xs text-primary-700 hover:underline ml-1"
            @click="clearAllFilters"
          >Clear all</button>
        </div>

        <!-- BULK BAR. Styled like Support_List's so the two surfaces read
             alike — same primary tint, same "N selected", same trailing Clear.
             Appears only with a non-empty selection: a permanently visible bar
             with nothing to act on is a control claiming to be armed.

             DUE DATE AND STATUS ONLY. Owner was asked for and explicitly
             deprioritised as not batch-shaped ("a distant third, and that tends
             to be more ad hoc than batch"), so it is absent on purpose rather
             than pending. -->
        <div
          v-if="tab === 'requirements' && checkedRequirements.size"
          class="flex items-center flex-wrap gap-3 px-4 py-2 mb-2 rounded-lg
                 bg-primary-50 border border-primary-200"
        >
          <span class="text-sm font-medium text-primary-700">
            {{ checkedRequirements.size }} selected
          </span>
          <select
            class="text-sm border border-primary-300 rounded px-2 py-1 bg-white"
            :disabled="bulkBusy"
            aria-label="Set status on the selected requirements"
            @change="bulkSetStatus($event.target.value); $event.target.value = ''"
          >
            <option value="">Set status…</option>
            <option v-for="value in bulkStatusOptions" :key="value" :value="value">
              {{ statusLabel(value) }}
            </option>
          </select>
          <div class="relative">
            <button
              type="button"
              class="text-sm border border-primary-300 rounded px-2 py-1
                     text-primary-700 hover:bg-primary-100 bg-white"
              :disabled="bulkBusy"
              @click.stop="bulkDropdown = bulkDropdown === 'due_date' ? null : 'due_date'"
            >
              Due date…
            </button>
            <div
              v-if="bulkDropdown === 'due_date'"
              class="absolute top-full left-0 mt-1 z-50 bg-white border
                     border-gray-200 rounded shadow-lg p-3"
              @click.stop
            >
              <input
                type="date"
                class="text-xs border border-gray-300 rounded px-2 py-1.5 w-full"
                aria-label="Due date for the selected requirements"
                @change="bulkSetDueDate($event.target.value); bulkDropdown = null"
              />
              <button
                type="button"
                class="w-full text-left text-xs text-gray-600 hover:text-gray-900 mt-2"
                @click="bulkSetDueDate(null); bulkDropdown = null"
              >
                Clear due date
              </button>
            </div>
          </div>
          <span v-if="bulkBusy" class="text-xs text-primary-700">Working…</span>
          <div class="flex-1" />
          <button
            type="button"
            class="text-xs text-gray-600 hover:text-gray-900"
            @click="clearChecked"
          >Clear</button>
        </div>

        <!-- WHAT DID NOT HAPPEN, PER ROW. A count ("3 of 10 failed") is enough
             for a due-date sweep and useless here: a stale verdict is a retry,
             an already-closed requirement is nothing to do, and a person who
             just closed ten rows changes their next move depending on which it
             was. The ids are listed because "which ones" is the question. -->
        <div
          v-if="bulkResult"
          class="px-4 py-3 mb-2 rounded-lg border border-gray-200 bg-white"
        >
          <div class="flex items-start gap-3">
            <div class="flex-1 text-[13px] text-gray-900 font-medium">
              {{ bulkCloseSummary(bulkResult) }}
            </div>
            <button
              type="button"
              class="text-xs text-gray-600 hover:text-gray-900"
              title="Dismiss"
              @click="bulkResult = null"
            >&times;</button>
          </div>
          <div
            v-if="bulkResult.stale.length"
            class="mt-2 text-xs text-gray-800 leading-relaxed"
          >
            <span class="font-semibold">Changed after Hades investigated:</span>
            {{ bulkResult.stale.map((r) => `#${r.requirement}`).join(', ') }} —
            the verdict describes a record that has since moved.
            <!-- ONE CONFIRMATION FOR THE GROUP, and acknowledge_stale is NEVER
                 sent on the first attempt: the guard exists so a human looks
                 before overriding it, and a batch that pre-acknowledged would
                 make the guard unreachable from this surface. -->
            <button
              type="button"
              class="ml-1 text-primary-700 hover:underline font-medium"
              :disabled="bulkBusy"
              @click="askRetryStale"
            >
              Close {{ bulkResult.stale.length }} anyway
            </button>
          </div>
          <p
            v-if="bulkResult.already.length"
            class="mt-2 text-xs text-gray-600 leading-relaxed"
          >
            <span class="font-semibold">Already closed:</span>
            {{ bulkResult.already.map((r) => `#${r.requirement}`).join(', ') }}.
          </p>
          <p
            v-if="bulkResult.failed.length"
            class="mt-2 text-xs text-red-dark leading-relaxed"
          >
            <span class="font-semibold">Failed:</span>
            {{ bulkResult.failed.map((r) => `#${r.requirement} ${r.detail}`).join(' · ') }}
          </p>
        </div>

        <!-- BOARD. Hidden for Deadlines: the rail is queue (Requirements) or
             work category (Engineering), and neither concept exists on a tab
             with no runPipeline() and no per-row selection, so it is hidden
             rather than rendered as an empty 224px column. -->
        <div
          v-if="boardTab"
          class="flex bg-white border border-gray-200 rounded-lg overflow-hidden"
          style="height: 560px"
        >
          <nav
            class="flex-none w-56 border-r border-gray-200 overflow-y-auto px-2 py-3"
          >
            <div
              class="px-2 pt-1 pb-2 text-[11px] font-semibold tracking-wide
                     uppercase text-gray-600"
            >{{ railTitle }}</div>

            <label
              v-if="tab === 'requirements'"
              class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer
                     select-none hover:bg-gray-50"
            >
              <input
                v-model="investigatedOnly"
                type="checkbox"
                class="w-3.5 h-3.5 accent-primary-500"
              />
              <span class="flex-1 text-[13px] text-gray-800">Investigated</span>
              <span class="text-xs text-gray-600">{{ investigatedCount }}</span>
            </label>

            <button
              v-for="item in rail"
              :key="item.value"
              type="button"
              class="w-full flex items-center justify-between gap-2 px-2 py-1.5
                     rounded text-[13px] text-left"
              :class="item.active ? 'bg-gray-50' : 'hover:bg-gray-50'"
              @click="selectRail(item.value)"
            >
              <span
                :class="item.active ? 'font-semibold text-gray-900' : 'text-gray-800'"
              >{{ item.label }}</span>
              <span class="text-xs text-gray-600">{{ item.count }}</span>
            </button>
          </nav>

          <main class="flex-1 min-w-0 overflow-auto">
            <Hades_TicketTable
              v-if="tab === 'requirements'"
              :tickets="visibleTickets"
              :selected-requirement="selectedRequirement"
              :checked="checkedRequirements"
              :sort-key="ticketSort.key"
              :sort-dir="ticketSort.dir"
              @select="selectTicket"
              @sort="sortTickets"
              @toggle-check="toggleCheck"
              @toggle-all="toggleAllVisible"
            />
            <Hades_PRTable
              v-else
              :pull-requests="visiblePrs"
              :selected-id="selectedPrId"
              :sort-key="prSort.key"
              :sort-dir="prSort.dir"
              @select="selectPr"
              @sort="sortPrs"
            />
          </main>

          <!-- MOUNTED ONLY WHEN SOMETHING IS SELECTED. It used to be permanent,
               so 384px of the board was spent on "Select a row to read the
               verdict" that could never be dismissed. Now the table gets the
               full width until you actually open a row. -->
          <Hades_Detail
            v-if="detailOpen"
            :ticket="tab === 'requirements' ? selectedTicket : null"
            :pull-request="tab === 'engineering' ? selectedPr : null"
            @closed="fetchPortfolio()"
            @close="closePanel"
          />
        </div>

        <!-- ZONES. Arithmetic over the customer pill's rows, grouped by the
             zone ancestor the backend resolved. Clicking a card lands on the
             Requirements tab with only the Zone filter set. -->
        <Hades_Zones
          v-else-if="tab === 'zones'"
          :tickets="customerTickets"
          :zones="zones"
          @select="selectZone"
        />

        <Hades_Deadlines
          v-else
          :deadlines="deadlines"
          :loading="deadlinesLoading"
          :error="deadlinesError"
        />
      </div>

      <ConfirmationModal
        :is-visible="!!pendingBulk"
        :message="bulkConfirmMessage"
        @confirm="runPendingBulk"
        @cancel="pendingBulk = null"
      />
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { listDeadlines, updateTicket } from '@/services/api/serviceDesk';
import { z } from 'zod';
import { mapGetters } from 'vuex';
import { useToast } from 'vue-toastification';
import { BrowserStore } from '@/utils/BrowserStore';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import { isDemoMode, spoofText } from '@/utils/demoSpoof';
import Hades_TicketTable from './Hades_TicketTable.vue';
import Hades_PRTable from './Hades_PRTable.vue';
import Hades_Detail from './Hades_Detail.vue';
import Hades_Charts from './Hades_Charts.vue';
import Hades_Deadlines from './Hades_Deadlines.vue';
import Hades_Zones from './Hades_Zones.vue';
import Hades_FilterSelect from './Hades_FilterSelect.vue';
import {
  BULK_SELECTABLE_STATUSES, DONE_STATUSES, STATUS_LABELS, calendarDayValue,
  formatDateValue, prioritySortValue, tenantColorFor,
} from '../support_constants';
import {
  ALREADY, FAILED, STALE, bulkCloseSummary, closeFailureDetail,
  closeFailureKind, emptyCloseResult,
} from './hades_bulk';
import {
  FILTER_QUERY_KEYS, FILTER_STATE_SCHEMA, HADES_FILTERS, QUICK_FILTERS,
  QUICK_FILTER_KEYS, emptyFilterState, emptyFilterValue, filterOptions,
  filtersFromQuery, filtersToQuery, hasSelection, quickFilterAdmits,
  rowMatchesFilters, searchRows,
} from './hades_filters';
import {
  CATEGORY_LABELS, QUEUE_LABELS, QUEUE_ORDER, comparator, label,
} from './hades_constants';

// THE STATUSES THE BULK BAR OFFERS. BULK_SELECTABLE_STATUSES is the list every
// bulk status menu reads — Support_List's and the service desk's — so 'draft'
// stays out (an authoring state, not something you triage into) and the deprecated 'resolved' stays out with it.
// WHICH of these is a close is decided by DONE_STATUSES, not by a second list
// here; see bulkSetStatus for why that distinction is load-bearing.
const BULK_STATUSES = BULK_SELECTABLE_STATUSES;

// Pinned pill count before the "N more" overflow, per app_ui/components.
const PINNED_CUSTOMERS = 4;

// Columns whose sort value is NOT the raw field. Anything not listed here sorts
// on row[key] directly.
const TICKET_ACCESSORS = {
  // Severity order, not alphabetical — 'none' > 'p0' as a string.
  // prioritySortValue() is the shared rule: legacy urgent/high/medium/low fold
  // onto their canonical code first, and unset ranks after p3, never before p0.
  requirement_priority: (r) => prioritySortValue(r.requirement_priority),
  // An array; sort on its first label so the column groups rather than
  // comparing "[object Array]" against itself.
  queues: (r) => (r.queues || [])[0] || null,
  // Booleans do not compare usefully as booleans. 0 = investigated, so an
  // ascending sort puts the rows with verdicts first.
  investigated: (r) => (r.investigated ? 0 : 1),
  // Dates must compare as NUMBERS. As ISO strings they happen to sort
  // correctly, but only while every value has the same format and offset —
  // one '+05:00' row and the ordering is silently wrong.
  requirement_created_at: (r) => (r.requirement_created_at
    ? new Date(r.requirement_created_at).getTime() : null),
  // calendarDayValue, NOT support_constants' dueDateSortValue. That one maps a
  // missing date to Infinity, which sorts last ASCENDING but FIRST descending —
  // undated rows would colonise the top of "furthest deadline first". The
  // comparator above already puts absent last in both directions, and it needs
  // a null to recognise absence.
  requirement_due_date: (r) => calendarDayValue(r.requirement_due_date),
};

// Sort keys a header can emit. A stored or URL-supplied key is checked against
// these before it is used: comparator() falls back to row[key], so an
// unrecognised key reads undefined on every row, sorts nothing, and still paints
// an arrow claiming the column is sorted.
const TICKET_SORT_KEYS = [
  'investigated', 'requirement', 'requirement_priority', 'requirement_type',
  'requirement_title', 'queues', 'delivery', 'tenant_name', 'owner_name',
  'creator_name', 'requirement_created_at', 'requirement_due_date',
];
const PR_SORT_KEYS = [
  'pull_request', 'title', 'delivery_state', 'customer_linkage', 'engineer_id',
  'merged_at',
];

const TABS = ['requirements', 'engineering', 'zones', 'deadlines'];

// Rail values a restored or link-supplied queue may take. QUEUE_ORDER, not
// QUEUE_LABELS: 'not_investigated' has a label but is deliberately not on the
// rail, so selecting it would filter the board with no rail item lit and no way
// to clear it short of editing the URL back.
const QUEUE_VALUES = ['all', ...QUEUE_ORDER];

// Board keys the URL may carry, beside FILTER_QUERY_KEYS. If a link holds ANY
// key from either set it wins over localStorage entirely, so a shared view
// reproduces what the sender saw rather than a merge with the reader's.
const BOARD_QUERY_KEYS = [
  'tab', 'customer', 'queue', 'category', 'investigated', 'q', 'quick',
  'sort', 'dir', 'psort', 'pdir',
];
const RECOGNISED_QUERY_KEYS = new Set([...BOARD_QUERY_KEYS, ...FILTER_QUERY_KEYS]);

const sortSchema = (keys) => z.object({
  key: z.enum(keys).nullable().catch(null),
  dir: z.enum(['asc', 'desc']).catch('asc'),
});

// EVERY FIELD .catch()es. A stored value that no longer type-checks degrades to
// its default instead of throwing on a read that happens before the first fetch
// resolves. Values that can only be validated against LIVE data — the customer
// pill, the queue key, a selected creator — are checked after the fetch lands
// instead; see fetchPortfolio and the filterOptionValues watcher.
const BOARD_STATE_SCHEMA = z.object({
  tab: z.enum(TABS).catch('requirements'),
  selectedCustomers: z.array(z.string()).catch(() => []),
  queueFilter: z.enum(QUEUE_VALUES).catch('all'),
  // Categories are NOT validated here: the category rail is built from the
  // data, not from a static list, so a category classified by a newer
  // cerberus config would be a legitimate selection that a static enum would
  // reject. It is checked against the loaded PRs in fetchPortfolio instead.
  categoryFilter: z.string().catch('all'),
  investigatedOnly: z.boolean().catch(false),
  quickFilter: z.enum(QUICK_FILTER_KEYS).nullable().catch(null),
  searchQuery: z.string().catch(''),
  filters: FILTER_STATE_SCHEMA,
  ticketSort: sortSchema(TICKET_SORT_KEYS).catch(() => ({ key: null, dir: 'asc' })),
  prSort: sortSchema(PR_SORT_KEYS).catch(() => ({ key: 'merged_at', dir: 'desc' })),
});

// HADES IS CROSS-TENANT — it reads every tenant's requirements at once, so
// unlike `support_list:<tenantSlug>:<userId>` the key is scoped on the USER
// alone. There is no tenant context here for one to leak into another. This is
// UI state, not a cached payload, so the bounding rule in
// engineering_guides/browser_store/OVERVIEW.txt ("keep one entry per prefix")
// does not apply: an entry is a few hundred bytes, not the ~1.8 MB an item
// payload measures.
const STATE_PREFIX = 'hades_board';

function boardStoreFor(userId) {
  // No partial keys. `hades_board:undefined` is worse than no cache — it is a
  // bucket every user on the machine shares.
  if (userId === null || userId === undefined) return null;
  return new BrowserStore(`${STATE_PREFIX}:${userId}`, BOARD_STATE_SCHEMA);
}

const PR_ACCESSORS = {
  // repo first, then number NUMERICALLY. The rendered "repo#123" string would
  // put #10 before #9.
  pull_request: (p) => `${p.repository}#${String(p.pr_number).padStart(8, '0')}`,
  merged_at: (p) => (p.merged_at ? new Date(p.merged_at).getTime() : null),
};

export default {
  name: 'Support_Hades',
  components: {
    LoadingIndicator, Hades_TicketTable, Hades_PRTable, Hades_Detail, Hades_Charts,
    Hades_Deadlines, Hades_Zones, Hades_FilterSelect, Tag, ConfirmationModal,
  },
  data() {
    return {
      loading: true,
      error: null,
      run: null,
      tickets: [],
      pullRequests: [],
      flow: [],
      customers: [],
      // The zone requirements, from the portfolio payload. The Zones tab lists
      // every one of them, rows or not.
      zones: [],
      // Requirements closed FROM this board in the trailing 30 days, total and
      // per customer. Seeded rather than left null so the stat renders 0 on the
      // first paint instead of blinking in after the fetch.
      closes: { total: 0, by_customer: {} },
      // MULTI-SELECT, and EMPTY MEANS EVERY CUSTOMER — not "no customer".
      // Named apart from `customers`, which is the API's per-tenant cards.
      selectedCustomers: [],
      tab: 'requirements',
      queueFilter: 'all',
      // DEFAULT OFF: unchecked shows everything. Ticking it narrows to the
      // requirements Hades has actually reached.
      investigatedOnly: false,
      // ONE OBJECT FOR EVERY DECLARED FILTER, keyed by HADES_FILTERS[].key.
      // Held as one value rather than a field per filter so the URL sync, the
      // stored state and the stale-selection watcher each touch it once.
      filters: emptyFilterState(),
      searchQuery: '',
      quickFilter: null,
      chartsExpanded: true,
      showMoreCustomers: false,
      // SELECTION IS KEYED ON requirement, NOT ticket id. An un-investigated
      // row has no HadesTicket, so its id is null — every one of them collided
      // on that null and selecting any highlighted all.
      selectedRequirement: null,
      // THE CHECKBOX SET, KEYED ON `requirement` FOR THE SAME REASON
      // selectedRequirement is: 184 of 404 rows have no HadesTicket and arrive
      // with id === null, so a set keyed on id would hold one entry for all of
      // them and a bulk write would hit every un-investigated row on the board.
      // Cleared on every context switch — see clearSelection.
      checkedRequirements: new Set(),
      bulkDropdown: null,
      bulkBusy: false,
      // The outcome of the last batch close, per row. Survives the selection
      // being cleared, because the answer is what you read AFTER the write.
      bulkResult: null,
      // { ids, status, acknowledgeStale } awaiting the group confirmation.
      pendingBulk: null,
      poll: null,
      categoryFilter: 'all',
      selectedPrId: null,
      // DEFAULTS MATCH WHAT THE BACKEND ALREADY ORDERS BY, so the first render
      // is not a silent re-sort of what the API sent. Requirements arrive
      // -updated_at; PRs arrive newest-merged-first in spirit, so Merged desc.
      ticketSort: { key: null, dir: 'asc' },
      prSort: { key: 'merged_at', dir: 'desc' },
      // DEADLINES READS A SEPARATE ENDPOINT, outside runPipeline() and outside
      // this component's own polling — a different scope (all open work, not
      // HADES_SCOPE_*) that the customer pill, filters, search and quick
      // filters do not apply to. null until first visited: FETCHED LAZILY on
      // the tab watcher below rather than in created(), so opening the board
      // on Requirements or Engineering does not pay for a payload nobody is
      // looking at.
      deadlines: null,
      deadlinesLoading: false,
      deadlinesError: null,
    };
  },
  computed: {
    demoMasking() {
      return isDemoMode();
    },
    ...mapGetters('auth', ['current_user']),
    toast() {
      return useToast();
    },
    bulkStatusOptions() {
      return BULK_STATUSES;
    },
    bulkConfirmMessage() {
      const pending = this.pendingBulk;
      if (!pending) return '';
      const ids = pending.ids.map((id) => `#${id}`).join(', ');
      const head = [
        `Close ${pending.ids.length} requirement`
          + `${pending.ids.length === 1 ? '' : 's'} as `
          + `${label(STATUS_LABELS, pending.status)}: ${ids}`,
        '',
      ];
      if (pending.acknowledgeStale) {
        return head.concat([
          'These changed after Hades investigated them, so each verdict '
          + 'describes a record that has since moved. Closing anyway '
          + 'acknowledges that and posts the verdict as it stands.',
        ]).join('\n');
      }
      return head.concat([
        'Each one is closed on its own verdict, with that verdict\'s evidence '
        + 'posted as an internal note. A requirement that moved since Hades '
        + 'looked at it will be reported back rather than closed.',
      ]).join('\n');
    },
    // Rebuilt if the user changes under a long-lived tab; null until the id is
    // known, which skips both the read and the write rather than writing a
    // shared key.
    boardStore() {
      return boardStoreFor(this.current_user?.id);
    },
    // Everything the URL and localStorage carry, in one place, so the two can
    // never drift on which fields count as "the view".
    boardState() {
      return {
        tab: this.tab,
        selectedCustomers: this.selectedCustomers,
        queueFilter: this.queueFilter,
        categoryFilter: this.categoryFilter,
        investigatedOnly: this.investigatedOnly,
        quickFilter: this.quickFilter,
        searchQuery: this.searchQuery,
        filters: this.filters,
        ticketSort: this.ticketSort,
        prSort: this.prSort,
      };
    },
    metaLine() {
      if (!this.run) return 'No completed run yet — showing requirements only.';
      const when = this.run.completed_at
        ? new Date(this.run.completed_at).toLocaleString() : 'in progress';
      return `Run ${this.run.id} · ${this.run.model} · ${when}`;
    },
    // Stats follow the customer filter too. Reading the API's whole-database
    // aggregates here would leave the headline numbers unchanged while the table
    // below them showed one tenant.
    stats() {
      const rows = this.customerTickets;
      const prs = this.customerPrs;
      const inQueue = (queue) => rows.filter(
        (r) => (r.queues || []).includes(queue)).length;
      const unlinked = prs.filter((p) => !(p.requirement_ids || []).length).length;
      return [
        { label: 'Requirements', value: rows.length },
        // From the FLAG, not inQueue('not_investigated') — that queue is no
        // longer in QUEUE_ORDER, and a count that depends on a rail label
        // silently breaks when the label moves.
        { label: 'Investigated', value: `${rows.filter((r) => r.investigated).length} / ${rows.length}` },
        { label: 'Ready to close', value: inQueue('shipped_record_stale') },
        // FOLLOWS THE CUSTOMER FILTER like every other stat in this row. The
        // backend sends a whole-database total alongside a per-customer
        // breakdown precisely so this one does not sit frozen above a board
        // showing one tenant. by_customer is keyed on
        // coalesce(tenant.name, 'Platform') — the same convention the pills are
        // built from, so the selected pill label is a direct key hit, and a
        // customer with no closes in the window is absent rather than 0.
        { label: 'Closed from Hades · 30d', value: this.closesInScope },
        { label: 'Unnoticed', value: inQueue('unnoticed') },
        { label: 'Unknown', value: inQueue('needs_evidence') },
        { label: 'PRs unlinked', value: `${unlinked} / ${prs.length}` },
      ];
    },
    // SUMMED OVER THE SELECTED PILLS, not read from one key. by_customer is
    // keyed on coalesce(tenant.name, 'Platform') — the same convention
    // _customer_cards builds the pills from — so each label is a direct hit and
    // a customer with no closes in the window is absent rather than 0. With no
    // pill selected this is the whole-database total, which is the only case
    // where closes.total is the right number.
    closesInScope() {
      if (!this.selectedCustomers.length) return this.closes.total || 0;
      return this.selectedCustomers.reduce(
        (sum, name) => sum + (this.closes.by_customer?.[name] || 0), 0);
    },
    tabs() {
      return [
        { key: 'requirements', label: 'Requirements', count: this.tickets.length },
        { key: 'engineering', label: 'Engineering', count: this.pullRequests.length },
        { key: 'zones', label: 'Zones', count: this.zones.length },
        // Blank rather than 0 until fetched — this reads a different endpoint
        // that is not loaded until the tab is first opened, and "0 due" would
        // be a claim, not an unknown.
        { key: 'deadlines', label: 'Deadlines', count: this.deadlines?.totals?.count ?? '' },
      ];
    },
    railTitle() {
      return this.tab === 'requirements' ? 'Queue' : 'Category';
    },
    // The two tabs with a rail, a table and a chart. Zones and Deadlines have
    // none of those and render their own surface instead.
    boardTab() {
      return this.tab === 'requirements' || this.tab === 'engineering';
    },
    // Rows the customer filter admits, BEFORE the queue/category filter. The
    // rail counts these — not the API's whole-database aggregate — so clicking a
    // tenant updates the rail instead of leaving stale totals beside a filtered
    // table.
    customerTickets() {
      if (!this.selectedCustomers.length) return this.tickets;
      return this.tickets.filter(
        (t) => this.selectedCustomers.includes(t.tenant_name || 'Platform'));
    },
    customerPrs() {
      // THE CUSTOMER FILTER DOES NOT APPLY TO PRs, deliberately.
      //
      // A PR's only link to a customer is the requirements it implements, and
      // only 7 of 357 have one — that ratio IS the finding this page exists to
      // show. Filtering PRs by tenant therefore empties the Engineering tab and
      // hides the 350 unlinked ones, which is exactly backwards. The customer
      // filter scopes requirements; engineering is org-wide by nature.
      return this.pullRequests;
    },
    unlinkedPrs() {
      return this.customerPrs.filter((p) => !(p.requirement_ids || []).length).length;
    },
    rail() {
      const isReq = this.tab === 'requirements';
      // COUNTS MUST MATCH THE TABLE. Using customerTickets here would leave
      // "All 402" above a 154-row table whenever the checkbox is off.
      const rows = isReq ? this.scopedTickets : this.customerPrs;
      const labels = isReq ? QUEUE_LABELS : CATEGORY_LABELS;
      const active = isReq ? this.queueFilter : this.categoryFilter;

      const counts = {};
      rows.forEach((row) => {
        if (isReq) {
          (row.queues || []).forEach((q) => { counts[q] = (counts[q] || 0) + 1; });
        } else {
          const c = row.primary_category || 'unclassified';
          counts[c] = (counts[c] || 0) + 1;
        }
      });

      // Queues follow QUEUE_ORDER (by what demands attention). Categories have
      // no natural priority, so they sort by volume.
      //
      // THE SELECTED RAIL ITEM IS ALWAYS LISTED, EVEN AT ZERO. Empty rows are
      // hidden because a rail of ten zeroes is noise — but hiding the one that
      // is CURRENTLY SELECTED takes the filter off screen while leaving it on:
      // pick Active, then narrow Type to Feature until no Active row survives,
      // and the rail shows no highlight, the chip row says nothing about a
      // queue, and an empty table sits under a total that looks like it should
      // have rows. The selection is still in the URL and in localStorage, so it
      // came back on the next visit with nothing on screen explaining it.
      const keep = (key) => counts[key] || key === active;
      const keys = isReq
        ? QUEUE_ORDER.filter(keep)
        : [...new Set([...Object.keys(counts), ...(active === 'all' ? [] : [active])])]
          .sort((a, b) => (counts[b] || 0) - (counts[a] || 0));

      return [
        { value: 'all', label: 'All', count: rows.length, active: active === 'all' },
        ...keys.map((key) => ({
          value: key,
          label: label(labels, key),
          count: counts[key] || 0,
          active: active === key,
        })),
      ];
    },
    shownCustomers() {
      if (this.showMoreCustomers) return this.customers;
      const pinned = this.customers.slice(0, PINNED_CUSTOMERS);
      // A SELECTED CUSTOMER IS NEVER HIDDEN BEHIND "N more". Same failure as the
      // rail dropping its selected queue: the filter stays applied while the
      // control that says so goes off screen, and the selection outlives the
      // visit through the URL and localStorage. Selecting from the expanded
      // list and collapsing it again is the ordinary way to reach this.
      const selectedOffList = this.customers.filter(
        (card) => this.selectedCustomers.includes(card.customer)
          && !pinned.includes(card));
      return [...pinned, ...selectedOffList];
    },
    hiddenCustomerCount() {
      return this.customers.length - this.shownCustomers.length;
    },
    // One control per `set` filter, each carrying its own volume-ordered option
    // list. Built from the definitions, so the markup never names a filter.
    setFilterControls() {
      return HADES_FILTERS
        .filter((def) => def.kind === 'set')
        .map((def) => ({
          def,
          options: filterOptions(def, this.customerTickets, this.facetRows(def.key)),
        }));
    },
    dateFilterDefs() {
      return HADES_FILTERS.filter((def) => def.kind === 'date');
    },
    // The owner name a portfolio row would carry for the signed-in user. The
    // row has no owner id, only owner_name from get_full_name(), so the display
    // name is the only join available.
    myOwnerName() {
      const user = this.current_user;
      const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
      return name || null;
    },
    quickFilterOptions() {
      // "Mine" is not offered when there is nothing to join against — a control
      // that is always empty is worse than no control.
      // Needs reply carries its count on the button: it is the one quick filter that
      // is a list of things owed right now, and "how many" is the question
      // before "which". Through the same pipeline, so it agrees with the table.
      return [
        { key: null, label: 'All' },
        ...QUICK_FILTERS
          .filter((q) => q.key !== 'mine' || this.myOwnerName)
          .map((q) => (q.key === 'needs_reply'
            ? { ...q, count: this.runPipeline(this.filters, { quick: 'needs_reply' }).length }
            : q)),
      ];
    },
    // ONE REMOVABLE CHIP PER APPLIED VALUE. Four dropdowns, a search box and a
    // segmented control with no chip row means the applied state is spread
    // across six places and none of them says what the whole view is.
    filterChips() {
      const chips = [];
      for (const def of HADES_FILTERS) {
        if (!hasSelection(def, this.filters)) continue;
        const value = this.filters[def.key];
        if (def.kind === 'date') {
          // Rendered through formatDateValue, so a bare calendar day reads as
          // the day it is rather than the day before it west of UTC.
          if (value.on_or_after) {
            chips.push({
              id: `${def.key}:on_or_after`,
              label: `${def.label} on or after ${formatDateValue(value.on_or_after)}`,
              remove: () => this.setDateBound(def.key, 'on_or_after', ''),
            });
          }
          if (value.on_or_before) {
            chips.push({
              id: `${def.key}:on_or_before`,
              label: `${def.label} on or before ${formatDateValue(value.on_or_before)}`,
              remove: () => this.setDateBound(def.key, 'on_or_before', ''),
            });
          }
          continue;
        }
        for (const selected of value) {
          const shown = def.optionLabel ? def.optionLabel(selected) : selected;
          chips.push({
            id: `${def.key}:${selected}`,
            // Tag renders the label in one span, so the value cannot carry a
            // `sensitive` class of its own without masking the whole chip.
            // Spoofed here instead; spoofText shares the DOM engine's name map,
            // so the chip matches the masked cells beside it.
            label: `${def.label} ${def.sensitive?.(selected) && isDemoMode()
              ? spoofText(shown) || 'Redacted' : shown}`,
            remove: () => this.setFilter(def.key, value.filter((v) => v !== selected)),
          });
        }
      }
      // Search and the quick filter get chips too. Both have their own visible
      // control, but "Clear all" clears them, and a Clear all that resets
      // something with no chip beside it is a button with a hidden effect.
      if (this.quickFilter) {
        const quick = QUICK_FILTERS.find((q) => q.key === this.quickFilter);
        chips.push({
          id: `quick:${this.quickFilter}`,
          label: quick ? quick.label : this.quickFilter,
          remove: () => { this.quickFilter = null; },
        });
      }
      if (this.searchQuery.trim()) {
        chips.push({
          id: 'search',
          label: `Search "${this.searchQuery.trim()}"`,
          remove: () => { this.searchQuery = ''; },
        });
      }
      return chips;
    },
    // Just the option VALUES, for the stale-selection watcher. Separated from
    // setFilterControls so the watcher does not re-fire every time a faceted
    // count moves — only when a value actually appears or disappears.
    filterOptionValues() {
      const sets = {};
      for (const def of HADES_FILTERS) {
        if (def.kind !== 'set') continue;
        sets[def.key] = filterOptions(def, this.customerTickets, []).map((o) => o.value);
      }
      return sets;
    },
    // THE ONE FILTER PIPELINE. Customer, the Investigated gate and every
    // declared filter, applied ONCE. The rail counts and the table both read
    // this, so a rail total can never disagree with the rows beside it — they
    // were computed separately once, disagreed, and the board showed "All 402"
    // above a 154-row table.
    scopedTickets() {
      return this.runPipeline(this.filters);
    },
    investigatedCount() {
      // Everything the pipeline does EXCEPT the Investigated gate, so the number
      // describes the rows the checkbox would actually narrow. Counting
      // customerTickets left it at 154 beside a 12-row Cerberus-filtered table.
      return this.runPipeline(this.filters, { investigatedGate: false })
        .filter((r) => r.investigated).length;
    },
    visibleTickets() {
      // The checkbox gates on the `investigated` FLAG, not on a queue value, so
      // it stays correct if the not_investigated queue label ever changes.
      const rows = this.scopedTickets;
      const filtered = this.queueFilter === 'all'
        ? rows : rows.filter((t) => (t.queues || []).includes(this.queueFilter));
      return this.applySort(filtered, this.ticketSort, TICKET_ACCESSORS);
    },
    visiblePrs() {
      const rows = this.customerPrs;
      const filtered = this.categoryFilter === 'all'
        ? rows : rows.filter((p) => p.primary_category === this.categoryFilter);
      return this.applySort(filtered, this.prSort, PR_ACCESSORS);
    },
    selectedTicket() {
      return this.tickets.find(
        (t) => t.requirement === this.selectedRequirement) || null;
    },
    selectedPr() {
      return this.pullRequests.find((p) => p.id === this.selectedPrId) || null;
    },
    // Whether the side panel is mounted at all. Keyed on the ACTIVE TAB's own
    // selection, so switching tabs does not show an empty panel because the
    // other tab happens to have a row selected. Deadlines has no detail panel
    // at all — a leftover selectedPr from Engineering must not read as "open"
    // once the tab has switched away from a two-way ternary that no longer
    // covers every tab.
    detailOpen() {
      if (this.tab === 'requirements') return !!this.selectedTicket;
      if (this.tab === 'engineering') return !!this.selectedPr;
      return false;
    },
  },
  watch: {
    // ONE WATCHER FOR THE WHOLE VIEW rather than one per control. Writing the
    // URL and localStorage from the same object is what keeps a shared link and
    // a restored session showing the same board.
    boardState: {
      deep: true,
      handler(state) {
        this.syncStateToUrl();
        this.boardStore?.save(state);
      },
    },
    demoMasking() {
      this.syncStateToUrl();
    },
    // A link pasted into the address bar of an already-open board. Guarded
    // against our own $router.replace, which would otherwise re-parse what it
    // just wrote and clobber a change made in the same tick.
    '$route.query': {
      handler(next, prev) {
        if (this._syncGuard > 0) return;
        if (JSON.stringify(next) === JSON.stringify(prev)) return;
        this.parseQueryToState();
      },
    },
    // Same rule as setFilter(): narrowing the board drops the open row rather
    // than leaving a panel above a table that no longer lists it.
    searchQuery() {
      this.clearSelection();
    },
    // A TAB SWITCH IS A CONTEXT SWITCH. detailOpen is keyed on the active tab's
    // own selection, so the panel already looked after itself here — the
    // checkbox set does not: Engineering has no requirements to act on, so
    // leaving rows checked behind it means coming back to a bulk bar armed
    // against rows you have not looked at since.
    tab() {
      this.clearSelection();
      // FETCHED LAZILY, ONCE. A link or a restored session can land straight
      // on this tab (tab is validated by TABS/BOARD_STATE_SCHEMA like the
      // other two), so the fetch lives here rather than only behind a click,
      // but it does not re-fetch on every visit — the header Refresh button
      // covers that.
      if (this.tab === 'deadlines' && !this.deadlines && !this.deadlinesLoading) {
        this.fetchDeadlines();
      }
    },
    investigatedOnly() {
      this.clearSelection();
    },
    // ONE WATCHER FOR EVERY FILTER, stated over the definition list rather than
    // per field. A creator picked under one customer usually has no rows under
    // another, which left an empty table and a selected name that was not in
    // the list; four bespoke watchers would be four chances to get that wrong.
    //
    // It watches the OPTION VALUES rather than `customer`, so it also covers a
    // refetch that drops the last row carrying a selected value.
    //
    // IT MUST NOT RUN BEFORE THE FIRST PAYLOAD LANDS. Every option set is empty
    // while tickets is empty, and "this value has no rows" is indistinguishable
    // from "nothing has loaded" — restoring a link or a saved view sets the
    // filters in created(), which re-evaluates the sets and would drop every
    // one of them a tick before the fetch resolves. A board with no rows judges
    // nothing.
    filterOptionValues: {
      handler(sets) {
        if (!this.tickets.length) return;
        for (const def of HADES_FILTERS) {
          if (def.kind !== 'set') continue;
          const selection = this.filters[def.key];
          if (!selection.length) continue;
          const kept = selection.filter((v) => sets[def.key].includes(v));
          if (kept.length !== selection.length) this.setFilter(def.key, kept);
        }
      },
    },
  },
  created() {
    // Counts nested $router.replace / parse passes. A plain boolean would be
    // cleared by the inner one and let the outer write through.
    this._syncGuard = 0;
    this._urlSyncPending = false;
    // URL WINS OVER localStorage ENTIRELY when it carries any recognised key.
    // Not a merge: a link is someone saying "look at this view", and a merge
    // would show the reader their own saved filters on top of it.
    if (Object.keys(this.$route.query).some((k) => RECOGNISED_QUERY_KEYS.has(k))) {
      this.parseQueryToState();
    } else {
      this.restoreState();
    }
    this.fetchPortfolio();
    // POLL WHILE A RUN IS IN FLIGHT. fetchPortfolio ran only on created(), so a
    // page opened before/during a run showed a frozen count until a manual
    // reload — indistinguishable from "nothing is being investigated".
    // SILENT: see fetchPortfolio — a visible spinner here unmounts the board.
    this.poll = setInterval(() => {
      if (this.run?.status === 'running') this.fetchPortfolio({ silent: true });
    }, 30000);
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
    // The due-date panel is absolutely positioned over the board, so it needs
    // to shut on an outside click the way Support_List's does — the button and
    // the panel itself both .stop.
    this._closeBulkDropdown = () => { this.bulkDropdown = null; };
    document.addEventListener('click', this._closeBulkDropdown);
  },
  beforeUnmount() {
    if (this.poll) clearInterval(this.poll);
    window.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this._closeBulkDropdown);
  },
  methods: {
    restoreState() {
      const saved = this.boardStore?.load();
      if (!saved) return;
      this.tab = saved.tab;
      this.selectedCustomers = saved.selectedCustomers;
      this.queueFilter = saved.queueFilter;
      this.categoryFilter = saved.categoryFilter;
      this.investigatedOnly = saved.investigatedOnly;
      this.quickFilter = saved.quickFilter;
      this.searchQuery = saved.searchQuery;
      // WHOLESALE, INCLUDING AN EMPTY ARRAY. A `if (saved.length)` guard would
      // silently re-add a selection the user deliberately cleared.
      this.filters = saved.filters;
      this.ticketSort = saved.ticketSort;
      this.prSort = saved.prSort;
    },
    parseQueryToState() {
      const q = this.$route.query;
      const one = (value) => (Array.isArray(value) ? value[0] : value) || '';
      this._syncGuard++;

      // Every value below is validated against a known set before use. An
      // unrecognised tab would render the wrong table; an unrecognised sort key
      // would paint an arrow over a column that is not sorted.
      const tab = one(q.tab);
      this.tab = TABS.includes(tab) ? tab : 'requirements';
      // Same comma encoding as every set filter. A tenant name with a comma in
      // it would split; none do, and the pills are built from the same strings
      // the backend coalesces, so a value round-trips unchanged.
      const customerParam = one(q.customer);
      this.selectedCustomers = customerParam
        ? customerParam.split(',').filter(Boolean) : [];
      const queue = one(q.queue);
      this.queueFilter = QUEUE_VALUES.includes(queue) ? queue : 'all';
      // Not validated here — see BOARD_STATE_SCHEMA; fetchPortfolio checks it
      // against the categories the loaded PRs actually carry.
      this.categoryFilter = one(q.category) || 'all';
      this.investigatedOnly = one(q.investigated) === '1';
      const quick = one(q.quick);
      this.quickFilter = QUICK_FILTER_KEYS.includes(quick) ? quick : null;
      this.searchQuery = one(q.q);
      this.filters = filtersFromQuery(q);
      this.ticketSort = this.parseSort(one(q.sort), one(q.dir), TICKET_SORT_KEYS,
        { key: null, dir: 'asc' });
      this.prSort = this.parseSort(one(q.psort), one(q.pdir), PR_SORT_KEYS,
        { key: 'merged_at', dir: 'desc' });

      // A link addresses a view, not a row.
      this.clearSelection();
      this.$nextTick(() => { this._syncGuard--; });
    },
    parseSort(key, dir, allowed, fallback) {
      if (!allowed.includes(key)) return { ...fallback };
      return { key, dir: dir === 'desc' ? 'desc' : 'asc' };
    },
    syncStateToUrl() {
      if (this._syncGuard > 0) {
        this._urlSyncPending = true;
        return;
      }
      this._urlSyncPending = false;
      const query = { ...filtersToQuery(this.filters) };
      // ONLY NON-DEFAULTS. A link that spells out every default is unreadable
      // and pins the reader to today's defaults if one ever changes.
      if (this.tab !== 'requirements') query.tab = this.tab;
      if (this.selectedCustomers.length) {
        query.customer = this.selectedCustomers.join(',');
      }
      if (this.queueFilter !== 'all') query.queue = this.queueFilter;
      if (this.categoryFilter !== 'all') query.category = this.categoryFilter;
      if (this.investigatedOnly) query.investigated = '1';
      if (this.quickFilter) query.quick = this.quickFilter;
      if (this.searchQuery.trim()) query.q = this.searchQuery.trim();
      if (this.ticketSort.key) {
        query.sort = this.ticketSort.key;
        query.dir = this.ticketSort.dir;
      }
      if (this.prSort.key && !(this.prSort.key === 'merged_at' && this.prSort.dir === 'desc')) {
        query.psort = this.prSort.key;
        query.pdir = this.prSort.dir;
      }
      // Demo Mode: tenant names and requester names/emails would sit in the
      // address bar, which the DOM mask cannot reach. They stay in board state
      // (and come back to the URL when Demo Mode ends).
      if (this.demoMasking) {
        delete query.customer;
        delete query.creator;
      }

      const current = this.$route.query;
      const keys = new Set([...Object.keys(query), ...Object.keys(current)]);
      const changed = [...keys].some(
        (k) => String(query[k] ?? '') !== String(current[k] ?? ''));
      if (!changed) return;

      this._syncGuard++;
      this.$router.replace({ query })
        .catch(() => {})
        .finally(() => {
          this._syncGuard--;
          // A change landing while replace() was in flight was swallowed by the
          // guard; run once more so the URL is not one edit behind.
          if (this._urlSyncPending) this.syncStateToUrl();
        });
    },
    // The pipeline itself, parameterised so the option counts can re-run it with
    // one filter blanked and the Investigated count can re-run it with the gate
    // off. Every caller shares this body — that is what makes "the rail and the
    // table read the same pipeline" true by construction rather than by
    // vigilance.
    runPipeline(filters, { investigatedGate = true, quick = this.quickFilter } = {}) {
      let rows = this.customerTickets;
      if (investigatedGate && this.investigatedOnly) {
        rows = rows.filter((r) => r.investigated);
      }
      rows = rows.filter((r) => rowMatchesFilters(r, filters));
      // Quick filters are shorthands over this same pipeline, not a parallel
      // one, so the rail counts follow them too.
      if (quick) {
        const ctx = { userName: this.myOwnerName };
        rows = rows.filter((r) => quickFilterAdmits(r, quick, ctx));
      }
      // NEEDS REPLY IS A TO-DO LIST, so it comes out most-overdue first: the reply
      // that was due longest ago sits on top, whatever its priority. A column
      // sort still wins (applySort runs after this) and search still ranks.
      if (quick === 'needs_reply') {
        rows = [...rows].sort((a, b) => Date.parse(a.response_due_at) - Date.parse(b.response_due_at));
      }
      // SEARCH IS A FILTER LIKE ANY OTHER, folded in here rather than applied to
      // the rows on their way to the table — that is what keeps the rail counts
      // following it. It also RE-ORDERS: matchesSearch returns a rank, so the
      // exact id sits above a title hit above a mention in the verdict. The
      // ordering survives applySort(), which returns rows untouched while no
      // column is the active sort.
      return searchRows(rows, this.searchQuery);
    },
    // Rows the pipeline would return if `key` were not selected — the row set
    // the option counts for `key` are taken over. See filterOptions().
    facetRows(key) {
      return this.runPipeline({ ...this.filters, [key]: emptyFilterValue(key) });
    },
    // Assigns a NEW filters object rather than mutating one slot, so a single
    // watcher on `filters` sees every change (URL sync, commit 5).
    // CHANGING A FILTER CLEARS THE SELECTION — see selectRail.
    setFilter(key, value) {
      this.filters = { ...this.filters, [key]: value };
      this.clearSelection();
    },
    // A scalar bound, never routed through the array path. An empty date input
    // reads back as '' — stored as null so hasSelection() sees an unset bound
    // rather than a falsy value that still counts as one.
    setDateBound(key, operator, raw) {
      this.setFilter(key, { ...this.filters[key], [operator]: raw || null });
    },
    dateBoundsSet(key) {
      const value = this.filters[key];
      return !!(value.on_or_after || value.on_or_before);
    },
    emptyFilterValue,
    // TOGGLES, so two tenants can be held against each other. Clears the open
    // row like every other narrowing control.
    toggleCustomer(name) {
      this.selectedCustomers = this.selectedCustomers.includes(name)
        ? this.selectedCustomers.filter((n) => n !== name)
        : [...this.selectedCustomers, name];
      this.clearSelection();
    },
    clearCustomers() {
      this.selectedCustomers = [];
      this.clearSelection();
    },
    setQuickFilter(key) {
      this.quickFilter = key;
      this.clearSelection();
    },
    clearAllFilters() {
      this.filters = emptyFilterState();
      this.quickFilter = null;
      this.searchQuery = '';
      this.clearSelection();
    },
    // A zone card's count is over the customer pill's rows and nothing else,
    // so landing on the Requirements tab clears every other narrowing control
    // first — the number clicked is the number shown.
    selectZone(name) {
      this.filters = { ...emptyFilterState(), zone_name: [name] };
      this.quickFilter = null;
      this.searchQuery = '';
      this.queueFilter = 'all';
      this.investigatedOnly = false;
      this.tab = 'requirements';
      this.clearSelection();
    },
    // Derived from the name, so the same customer keeps the same colour on
    // every Support surface rather than being grey here and coloured there.
    tenantColorFor,
    // `silent` skips the loading flag. The 30s poll used to set loading = true,
    // which swapped the ENTIRE board for a spinner every half minute during a
    // run — losing scroll position, the open panel and any filter you were
    // reading. A background refresh must not unmount what you are looking at.
    async fetchPortfolio({ silent = false } = {}) {
      if (!silent) this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/hades/portfolio/');
        this.run = data.run;
        this.tickets = data.tickets || [];
        this.pullRequests = data.pull_requests || [];
        this.flow = data.flow || [];
        this.closes = data.closes || { total: 0, by_customer: {} };
        this.customers = data.customers || [];
        this.zones = data.zones || [];
        // A restored or link-supplied customer that no longer has rows would
        // filter the board to nothing above a pill row that does not contain
        // it. Only checkable once the fetch has landed — the same reason the
        // filter selections are validated by the filterOptionValues watcher
        // rather than at restore time.
        const known = new Set(this.customers.map((c) => c.customer));
        if (this.selectedCustomers.some((name) => !known.has(name))) {
          this.selectedCustomers = this.selectedCustomers.filter((n) => known.has(n));
        }
        // Same for a category the data no longer carries: the category rail is
        // built from primary_category on the loaded PRs, so an unknown value
        // would filter the Engineering tab to nothing with no rail item lit.
        // Checked against ALL PRs, not the filtered ones — a queue that is
        // legitimately empty under the current filters must stay selected.
        const categories = new Set(
          this.pullRequests.map((p) => p.primary_category || 'unclassified'));
        if (this.categoryFilter !== 'all' && !categories.has(this.categoryFilter)) {
          this.categoryFilter = 'all';
        }
        // DO NOT AUTO-SELECT. This used to open row 0 whenever nothing was
        // selected, so the page loaded with the panel already open and every
        // refetch re-opened it — there was no reachable "nothing selected"
        // state. A selection that no longer exists (the row was closed) is
        // dropped, which correctly shuts the panel.
        if (this.selectedRequirement !== null && !this.selectedTicket) {
          this.selectedRequirement = null;
        }
        if (this.selectedPrId !== null && !this.selectedPr) {
          this.selectedPrId = null;
        }
        // A CHECKED ROW THAT IS NO LONGER IN THE PAYLOAD IS DROPPED. Closing a
        // requirement takes it out of the Hades scope entirely
        // (HADES_SCOPE_STATUSES is the open statuses), and the 30s poll refetches
        // under whatever is on screen — so without this the bar would keep
        // counting rows that no longer exist. loadedChecked() already refuses
        // to write to them; this is so the count does not lie in the meantime.
        if (this.checkedRequirements.size) {
          const present = new Set(this.tickets.map((t) => t.requirement));
          if ([...this.checkedRequirements].some((id) => !present.has(id))) {
            this.checkedRequirements = new Set(
              [...this.checkedRequirements].filter((id) => present.has(id)));
          }
        }
      } catch (e) {
        this.error = e.response?.data?.detail || 'Could not load the Hades portfolio.';
      } finally {
        this.loading = false;
      }
    },
    // A SEPARATE ENDPOINT with a wider scope than the portfolio above — every
    // open requirement, not HADES_SCOPE_*. Not part of runPipeline() and not
    // polled: the run status that drives fetchPortfolio's 30s poll has
    // nothing to do with due dates, so a refetch here is manual only, via the
    // header Refresh button while this tab is active.
    async fetchDeadlines() {
      this.deadlinesLoading = true;
      this.deadlinesError = null;
      try {
        const data = await listDeadlines();
        this.deadlines = data;
      } catch (e) {
        this.deadlinesError = e.response?.data?.detail || 'Could not load deadlines.';
      } finally {
        this.deadlinesLoading = false;
      }
    },
    // TOGGLE, not set. Clicking the open row closes the panel, which is the
    // gesture people reach for before they look for an X.
    selectTicket(ticket) {
      this.selectedRequirement = this.selectedRequirement === ticket.requirement
        ? null : ticket.requirement;
    },
    // Same toggle as selectTicket, so both tables behave identically.
    selectPr(id) {
      this.selectedPrId = this.selectedPrId === id ? null : id;
    },
    // ALWAYS ON A COPY. `rows` is a computed's array; Array.prototype.sort
    // mutates in place, and mutating a reactive source inside a computed
    // re-triggers it — an infinite update loop, not a wrong order.
    applySort(rows, sort, accessors) {
      if (!sort.key) return rows;
      return [...rows].sort(comparator(sort.key, sort.dir, accessors));
    },
    // THREE STATES per column: asc -> desc -> unsorted. The third matters
    // because the API's own ordering (requirements by -updated_at) is a useful
    // view you otherwise could not get back without reloading the page.
    cycleSort(sort, key) {
      if (sort.key !== key) return { key, dir: 'asc' };
      if (sort.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    },
    sortTickets(key) {
      this.ticketSort = this.cycleSort(this.ticketSort, key);
    },
    sortPrs(key) {
      this.prSort = this.cycleSort(this.prSort, key);
    },
    // A CONTEXT SWITCH — customer pill, any filter, quick filter, search, rail,
    // tab, or a link parsed into the board. Drops the open row AND the checkbox
    // set, because a filter change that left invisible rows checked would point
    // the next bulk write at rows nobody can see: the bar would still say
    // "6 selected" over a table showing two of them. Support_List clears its
    // checkedIds on a context switch for the same reason, and says so.
    clearSelection() {
      this.closePanel();
      this.clearChecked();
      this.bulkResult = null;
    },
    // Dismissing the panel is NOT a context switch. Nothing about the board's
    // scope changed, so a selection made in order to act on it must survive
    // reading one row's verdict and shutting the panel again.
    closePanel() {
      this.selectedRequirement = null;
      this.selectedPrId = null;
    },
    clearChecked() {
      this.checkedRequirements = new Set();
      this.bulkDropdown = null;
    },
    // A NEW Set EVERY TIME. Mutating one in place does not re-run a computed or
    // a prop watcher, so the header checkbox and the bar would both go stale.
    toggleCheck(requirement) {
      const next = new Set(this.checkedRequirements);
      if (next.has(requirement)) {
        next.delete(requirement);
      } else {
        next.add(requirement);
      }
      this.checkedRequirements = next;
    },
    // "VISIBLE" IS visibleTickets — what the current filters admit — never
    // `tickets`. Selecting all of a 404-row payload from a 12-row filtered view
    // is not what the header checkbox above those 12 rows says it does.
    toggleAllVisible(select) {
      if (!select) {
        this.clearChecked();
        return;
      }
      this.checkedRequirements = new Set(
        this.visibleTickets.map((t) => t.requirement));
    },
    // Esc closes the panel — standard for a dismissable surface, and the thing
    // you try when a panel has no visible way out. It does not clear the
    // selection: see closePanel.
    onKeydown(event) {
      if (event.key === 'Escape' && this.detailOpen) this.closePanel();
    },
    pillClass(active) {
      return ['flex items-center gap-2 h-[30px] px-3 rounded-full text-xs border',
        active
          ? 'border-primary-500 bg-primary-100 text-primary-700 font-semibold'
          : 'border-gray-200 bg-white text-gray-800 font-medium hover:bg-gray-50'];
    },
    // A CUSTOMER PILL KEEPS ITS TENANT COLOUR WHETHER OR NOT IT IS SELECTED.
    // Colouring only the inactive ones would make the colour disappear at the
    // moment you are actually looking at that tenant; selection is a ring.
    customerPillClass(active) {
      return ['flex items-center gap-2 h-[30px] px-3 rounded-full text-xs border',
        active
          ? 'border-transparent ring-2 ring-primary-500 font-semibold'
          : 'border-transparent font-medium hover:opacity-80'];
    },
    badgeClass(active) {
      return active ? 'text-[11px] text-primary-700' : 'text-[11px] text-gray-600';
    },
    statusLabel(status) {
      return label(STATUS_LABELS, status);
    },
    bulkCloseSummary,
    // FILTERED AGAINST THE LOADED ROWS BEFORE ANY WRITE — the Work tab's
    // loadedSelection() rule. Against visibleTickets rather than the whole
    // payload, because "on screen" is the promise the checkbox made: a
    // requirement the pipeline no longer admits is one nobody can see, and a
    // stale entry in the set would write to it anyway. clearSelection() makes
    // this unreachable today; it stays because the next control added to this
    // board is one more chance to forget.
    loadedChecked() {
      const visible = new Set(this.visibleTickets.map((t) => t.requirement));
      return [...this.checkedRequirements].filter((id) => visible.has(id));
    },
    // ROUTED BY DESTINATION, and this is the whole delicacy of the feature.
    //
    // A status change into a DONE status must go through POST /api/hades/close/,
    // one call per row, NOT through the generic PATCH that Support_List's
    // _bulkPatch uses. Three things that endpoint does that a bare PATCH does
    // not, all of which matter to a batch:
    //
    //   1. It stamps the close as coming from Hades, which is what
    //      _close_throughput() counts to produce "Closed from Hades · 30d" —
    //      the stat this board is on probation against. Bulk-closing through
    //      PATCH would make the board undercount exactly the closes it is being
    //      judged on, so it would look less useful the more it was used.
    //   2. It posts the verdict's citations as an internal note. "Closed by
    //      Irene" ages badly; "PR #2472 merged, confirmed in Pylon #336" does
    //      not — and a batch is where that record is most likely to be the only
    //      thing left explaining the decision.
    //   3. It refuses on a stale read, per row.
    //
    // A non-done status has none of that to preserve: nothing is being closed,
    // there is no verdict to cite and no prior read to be stale against, so it
    // takes the same PATCH the Work tab's bulk bar does.
    bulkSetStatus(status) {
      if (!status) return;
      const ids = this.loadedChecked();
      if (!ids.length) {
        this.clearChecked();
        return;
      }
      if (!DONE_STATUSES.has(status)) {
        this._bulkPatch(ids, { status });
        return;
      }
      // CONFIRMED AS A GROUP. The single close behind the panel confirms too,
      // and a batch writes a status change plus an internal note per row — the
      // one gesture on this board with a consequence worth reading twice. The
      // non-done PATCH above is not confirmed, matching Support_List, because
      // it is an ordinary field edit that the next edit undoes.
      this.pendingBulk = { ids, status, acknowledgeStale: false };
    },
    bulkSetDueDate(date) {
      const ids = this.loadedChecked();
      if (!ids.length) {
        this.clearChecked();
        return;
      }
      // Matches Support_List.bulkSetDueDate: an empty date input reads back as
      // '' and is sent as null, which is how the field is cleared.
      this._bulkPatch(ids, { due_date: date || null });
    },
    // The due-date and non-done-status path. A COUNT IS THE RIGHT REPORT HERE:
    // every row fails the same way (the request did not land), so which ones is
    // not a different question per row. That is precisely what makes it the
    // wrong report for a close — see _bulkClose.
    async _bulkPatch(ids, fields) {
      this.bulkBusy = true;
      const failed = [];
      try {
        for (const id of ids) {
          try {
            await updateTicket(id, fields);
          } catch {
            failed.push(id);
          }
        }
      } finally {
        this.bulkBusy = false;
      }
      if (failed.length) {
        this.toast.error(`${failed.length} of ${ids.length} updates failed`);
      }
      if (failed.length < ids.length) {
        this.toast.success(`${ids.length - failed.length} updated`);
      }
      this.clearChecked();
      // REFETCH AFTER A BATCH, so the rail counts, the stats row and the closes
      // stat describe what is now in the database rather than what was there
      // before the writes.
      await this.fetchPortfolio({ silent: true });
    },
    askRetryStale() {
      const result = this.bulkResult;
      if (!result?.stale.length) return;
      // acknowledge_stale is sent ONLY on this second, separately confirmed
      // attempt. Sending it on the first would make the guard unreachable from
      // this board — it exists so a human looks at what moved before overriding
      // the verdict, and one confirmation for the group is that look.
      this.pendingBulk = {
        ids: result.stale.map((row) => row.requirement),
        status: result.status,
        acknowledgeStale: true,
      };
    },
    runPendingBulk() {
      const pending = this.pendingBulk;
      this.pendingBulk = null;
      if (pending) this._bulkClose(pending);
    },
    // ONE CALL PER ROW against the single authorized close endpoint. There is no
    // bulk endpoint and this is not asking for one: the per-row stale guard and
    // the per-row cited note are the reasons the endpoint exists, and both are
    // per row by nature.
    async _bulkClose({ ids, status, acknowledgeStale }) {
      const result = emptyCloseResult(status);
      this.bulkBusy = true;
      try {
        for (const requirement of ids) {
          try {
            // requirement_id, not hades_ticket_id, because the checkbox set is
            // keyed on `requirement` and half the board has no ticket id at
            // all. The endpoint resolves the latest verdict for the
            // requirement itself and cites it, which is the same verdict the
            // board renders — the portfolio is latest-per-requirement too.
            await axios.post('/api/hades/close/',
              { requirement_id: requirement, status,
                ...(acknowledgeStale ? { acknowledge_stale: true } : {}) });
            result.closed.push(requirement);
          } catch (e) {
            const kind = closeFailureKind(e);
            const row = { requirement, detail: closeFailureDetail(e) };
            if (kind === STALE) result.stale.push(row);
            else if (kind === ALREADY) result.already.push(row);
            else if (kind === FAILED) result.failed.push(row);
          }
        }
      } finally {
        this.bulkBusy = false;
      }
      // NOT SWALLOWED INTO A COUNT. Reported per row, with the stale ones
      // separated from the already-closed ones, because a stale verdict is a
      // retry and an already-closed requirement is nothing to do.
      this.clearChecked();
      this.bulkResult = result;
      if (result.closed.length) await this.fetchPortfolio({ silent: true });
    },
    // CHANGING A FILTER CLEARS THE SELECTION, it does not pick a new row.
    // Auto-selecting row 0 here meant every rail click re-opened the panel, so
    // it was impossible to browse queues with the panel shut.
    selectRail(value) {
      if (this.tab === 'requirements') {
        this.queueFilter = value;
      } else {
        this.categoryFilter = value;
      }
      this.clearSelection();
    },
  },
};
</script>
